import { Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  Lock,
} from 'lucide-react';
import type { ReactNode } from 'react';

import type { PaymentRunReviewRow } from '~entities/payment/api/reporting-payment-run-review';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';

import { LOCK_CHECKBOX_CLASS_NAME } from './constants';
import { formatDueDateLabel, formatMoney } from './payment-run-format';
import { PaymentRunReviewMobileCards } from './payment-run-review-mobile-cards';
import type { PaymentRunReviewSortColumn } from './payment-run-review-sort';
import { paymentRunRowBackgroundClass } from './payment-run-status';
import {
  PaymentMethodCell,
  PaymentRunLockCheckbox,
  StatusCell,
} from './payment-run-table-cells';

function SortableColumnHeader({
  label,
  column,
  sortColumn,
  sortDirection,
  onSort,
  align = 'left',
}: {
  label: string;
  column: PaymentRunReviewSortColumn;
  sortColumn: PaymentRunReviewSortColumn;
  sortDirection: 'asc' | 'desc';
  onSort: (column: PaymentRunReviewSortColumn) => void;
  align?: 'left' | 'right';
}) {
  const active = sortColumn === column;
  const ariaSort = active
    ? sortDirection === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  return (
    <button
      type='button'
      aria-sort={ariaSort}
      onClick={() => {
        onSort(column);
      }}
      className={cn(
        'inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide',
        align === 'right' && 'w-full justify-end text-right',
      )}
    >
      <span>{label}</span>
      {active ? (
        sortDirection === 'asc' ? (
          <ArrowUp className='size-3.5 shrink-0 opacity-80' aria-hidden />
        ) : (
          <ArrowDown className='size-3.5 shrink-0 opacity-80' aria-hidden />
        )
      ) : (
        <ArrowUpDown className='size-3.5 shrink-0 opacity-35' aria-hidden />
      )}
    </button>
  );
}
function TruncatedCell({
  text,
  children,
  className,
}: {
  text: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      title={text || undefined}
      className={cn('block max-w-40 truncate', className)}
    >
      {children}
    </span>
  );
}

const columnHelper = createColumnHelper<PaymentRunReviewRow>();

interface PaymentRunReviewTableProps {
  isLoading: boolean;
  isError: boolean;
  onRefetch: () => void;
  lockHeaderAllChecked: boolean;
  lockHeaderIndeterminate: boolean;
  onToggleSelectAllLockable: () => void;
  lockInteractionsDisabled: boolean;
  lockableSelectableForBulk: Array<PaymentRunReviewRow>;
  paginatedRows: Array<PaymentRunReviewRow>;
  lockSelectionIds: Array<string>;
  incrementalLockAllowed: boolean;
  approvedForRunSet: Set<string>;
  lockableIds: Set<string>;
  onToggleLockSelection: (invoiceId: string, checked: boolean) => void;
  onOpenRowDetail: (row: PaymentRunReviewRow) => void;
  isPostponed: boolean;
  currencySymbol: string;
  filteredTotal: number;
  tablePageSize: number;
  setTablePageSize: (n: number) => void;
  tablePageSafe: number;
  tableTotalPages: number;
  setTablePage: (page: number | ((p: number) => number)) => void;
  idsForFooterApproveStats: Array<string>;
  tableFooterSelectionStats: {
    selectedSum: number;
    flaggedInSelection: number;
  };
  invoiceIdsPendingApprove: Array<string>;
  nextRunAt: string | undefined;
  lockSelectionApprovePreview: {
    chargesToProcess: number;
    totalAmount: number;
    flaggedCount: number;
    flaggedAmount: number;
    paymentMethodsSummary: string;
  } | null;
  onOpenApproveModalForSelection: () => void;
  paymentRunApproveLockEnabled: boolean;
  paymentRunPostponeCronEnabled: boolean;
  sortColumn: PaymentRunReviewSortColumn;
  sortDirection: 'asc' | 'desc';
  onRequestSort: (column: PaymentRunReviewSortColumn) => void;
}

export function PaymentRunReviewTable({
  isLoading,
  isError,
  onRefetch,
  lockHeaderAllChecked,
  lockHeaderIndeterminate,
  onToggleSelectAllLockable,
  lockInteractionsDisabled,
  lockableSelectableForBulk,
  paginatedRows,
  lockSelectionIds,
  incrementalLockAllowed,
  approvedForRunSet,
  lockableIds,
  onToggleLockSelection,
  onOpenRowDetail,
  isPostponed,
  currencySymbol: symbol,
  filteredTotal,
  tablePageSize,
  setTablePageSize,
  tablePageSafe,
  setTablePage,
  idsForFooterApproveStats,
  tableFooterSelectionStats,
  invoiceIdsPendingApprove,
  nextRunAt,
  lockSelectionApprovePreview,
  onOpenApproveModalForSelection,
  paymentRunApproveLockEnabled,
  paymentRunPostponeCronEnabled: _paymentRunPostponeCronEnabled,
  sortColumn,
  sortDirection,
  onRequestSort,
}: PaymentRunReviewTableProps) {
  const header = (
    label: string,
    column: PaymentRunReviewSortColumn,
    align?: 'left' | 'right',
  ) => (
    <SortableColumnHeader
      label={label}
      column={column}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={onRequestSort}
      align={align}
    />
  );
  const columns = [
    columnHelper.display({
      id: 'lock',
      header: () => (
        <Checkbox
          aria-label='Select all lockable charges in current filter'
          checked={lockHeaderAllChecked}
          indeterminate={lockHeaderIndeterminate}
          onCheckedChange={onToggleSelectAllLockable}
          disabled={
            lockInteractionsDisabled || lockableSelectableForBulk.length === 0
          }
          className={LOCK_CHECKBOX_CLASS_NAME}
        />
      ),
      cell: ({ row }) => (
        <PaymentRunLockCheckbox
          invoiceId={row.original.id}
          parentLabel={row.original.parent}
          lockSelectionIds={lockSelectionIds}
          incrementalLockAllowed={incrementalLockAllowed}
          approvedForRunSet={approvedForRunSet}
          lockableIds={lockableIds}
          lockInteractionsDisabled={lockInteractionsDisabled}
          onToggleLockSelection={onToggleLockSelection}
        />
      ),
    }),
    columnHelper.display({
      id: 'details',
      header: 'Details',
      cell: ({ row }) => (
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={() => {
            onOpenRowDetail(row.original);
          }}
          aria-label={`View details for ${row.original.parent || 'charge'}`}
        >
          <ChevronRight className='size-4' />
        </Button>
      ),
    }),
    columnHelper.accessor('parent', {
      header: () => header('Parent', 'parent'),
      cell: ({ row }) => (
        <TruncatedCell text={row.original.parent}>
          {row.original.parentId ? (
            <Link
              to='/family'
              search={{ parentId: row.original.parentId }}
              className='font-medium text-blue-600 hover:underline'
            >
              {row.original.parent || '—'}
            </Link>
          ) : (
            row.original.parent || '—'
          )}
        </TruncatedCell>
      ),
    }),
    columnHelper.accessor('student', {
      header: () => header('Student', 'student'),
      cell: ({ row }) => (
        <TruncatedCell text={row.original.student}>
          {row.original.studentId ? (
            <Link
              to='/students/$studentId/new-payments'
              params={{ studentId: row.original.studentId }}
              className='text-blue-600 hover:underline'
            >
              {row.original.student || '—'}
            </Link>
          ) : (
            row.original.student || '—'
          )}
        </TruncatedCell>
      ),
    }),
    columnHelper.accessor('dueDate', {
      header: () => header('Due date', 'dueDate'),
      cell: (info) => {
        const value = info.getValue();
        return value ? formatDueDateLabel(value) : '—';
      },
    }),
    columnHelper.accessor(
      (row) => [row.class, row.session].filter(Boolean).join(' / '),
      {
        id: 'classSession',
        header: () => header('Class / session', 'classSession'),
        cell: (info) => (
          <TruncatedCell text={info.getValue()} className='max-w-64'>
            {info.getValue() || '—'}
          </TruncatedCell>
        ),
      },
    ),
    columnHelper.accessor('billingType', {
      header: () => header('Type', 'billingType'),
      cell: (info) => info.getValue() || '—',
    }),
    columnHelper.accessor('baseAmount', {
      header: () => header('Base', 'baseAmount', 'right'),
      cell: (info) => formatMoney(info.getValue(), symbol),
    }),
    columnHelper.accessor('discount', {
      header: () => header('Discount', 'discount', 'right'),
      cell: (info) => formatMoney(info.getValue(), symbol),
    }),
    columnHelper.accessor('finalAmount', {
      header: () => header('Final', 'finalAmount', 'right'),
      cell: (info) => formatMoney(info.getValue(), symbol),
    }),
    columnHelper.accessor('transactionFee', {
      header: () => header('Transaction fee', 'transactionFee', 'right'),
      cell: (info) => formatMoney(info.getValue(), symbol),
    }),
    columnHelper.accessor('paidDate', {
      header: () => header('Paid date', 'paidDate'),
      cell: (info) => {
        const value = info.getValue();
        return value ? formatDueDateLabel(value) : '—';
      },
    }),
    columnHelper.accessor('paidAmount', {
      header: () => header('Paid amount', 'paidAmount', 'right'),
      cell: (info) =>
        info.getValue() > 0 ? formatMoney(info.getValue(), symbol) : '—',
    }),
    columnHelper.display({
      id: 'payment',
      header: () => header('Payment', 'payment'),
      cell: ({ row }) => (
        <PaymentMethodCell
          paymentMethod={row.original.paymentMethod}
          cardLast4={row.original.cardLast4}
        />
      ),
    }),
    columnHelper.accessor('status', {
      header: () => header('Status', 'status'),
      cell: ({ row }) => (
        <div className='flex flex-wrap items-center gap-1.5'>
          <StatusCell
            status={row.original.status}
            failureReason={row.original.failureReason}
          />
          {isPostponed ? (
            <span className='rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800'>
              Postponed
            </span>
          ) : null}
        </div>
      ),
    }),
  ];
  const pagination: PaginationState = {
    pageIndex: tablePageSafe - 1,
    pageSize: tablePageSize,
  };

  return (
    <Card className='overflow-hidden'>
      <CardContent className='min-w-0 p-0'>
        {isLoading ? (
          <div className='flex h-48 items-center justify-center'>
            <div className='size-8 animate-spin rounded-full border-4 border-muted border-t-primary' />
          </div>
        ) : isError ? (
          <div className='flex flex-col items-center justify-center gap-2 p-8'>
            <p className='text-destructive'>
              Error loading report. Please try again.
            </p>
            <Button
              variant='outline'
              onClick={() => {
                onRefetch();
              }}
            >
              Retry
            </Button>
          </div>
        ) : (
          <>
            <EnrollioTable
              className='p-4'
              density='compact'
              totalLabel='Payment run charges'
              filterHeader={(table) => (
                <EnrollioTableSearch
                  table={table}
                  placeholder='Search current page...'
                />
              )}
              data={paginatedRows}
              columns={columns}
              rowCount={filteredTotal}
              manualPagination
              pagination={pagination}
              onPaginationChange={(updater) => {
                const next =
                  typeof updater === 'function' ? updater(pagination) : updater;
                if (next.pageSize !== tablePageSize) {
                  setTablePageSize(next.pageSize);
                  setTablePage(1);
                } else {
                  setTablePage(next.pageIndex + 1);
                }
              }}
              getRowId={(row) => row.id}
              rowClassName={(row) =>
                paymentRunRowBackgroundClass(row.original.status)
              }
              mobile={(table, _updating) => (
                <PaymentRunReviewMobileCards
                  rows={table.getRowModel().rows.map((row) => row.original)}
                  lockHeaderAllChecked={lockHeaderAllChecked}
                  lockHeaderIndeterminate={lockHeaderIndeterminate}
                  onToggleSelectAllLockable={onToggleSelectAllLockable}
                  lockInteractionsDisabled={lockInteractionsDisabled}
                  lockableSelectableForBulkCount={
                    lockableSelectableForBulk.length
                  }
                  lockSelectionIds={lockSelectionIds}
                  incrementalLockAllowed={incrementalLockAllowed}
                  approvedForRunSet={approvedForRunSet}
                  lockableIds={lockableIds}
                  onToggleLockSelection={onToggleLockSelection}
                  onOpenRowDetail={onOpenRowDetail}
                  isPostponed={isPostponed}
                  currencySymbol={symbol}
                />
              )}
              isUpdating={isLoading}
              emptyContent='No rows match the current filter.'
              ariaLabel='Payment run charges'
            />

            {filteredTotal > 0 && (
              <div className='flex flex-col gap-3 border-t border-border bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
                <p className='text-sm text-muted-foreground'>
                  {filteredTotal} total
                  <span aria-hidden> · </span>
                  <span className='whitespace-nowrap'>
                    <strong className='font-semibold text-foreground'>
                      {idsForFooterApproveStats.length}
                    </strong>{' '}
                    {incrementalLockAllowed ? 'to add' : 'selected'}
                  </span>
                  <span aria-hidden> · </span>
                  <span className='whitespace-nowrap'>
                    <strong className='font-semibold text-foreground'>
                      {formatMoney(
                        tableFooterSelectionStats.selectedSum,
                        symbol,
                      )}
                    </strong>
                  </span>
                </p>
                <div className='flex min-h-8 flex-1 items-center justify-center'>
                  {tableFooterSelectionStats.flaggedInSelection > 0 ? (
                    <div
                      className='inline-flex max-w-full items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-950'
                      role='status'
                    >
                      <AlertTriangle
                        className='size-4 shrink-0 text-amber-600'
                        aria-hidden
                      />
                      <span>
                        <strong>
                          {tableFooterSelectionStats.flaggedInSelection}
                        </strong>{' '}
                        flagged item
                        {tableFooterSelectionStats.flaggedInSelection !== 1
                          ? 's'
                          : ''}{' '}
                        included — review recommended
                      </span>
                    </div>
                  ) : null}
                </div>
                <div className='flex shrink-0 justify-end sm:min-w-44'>
                  <Button
                    type='button'
                    size='sm'
                    className='w-full bg-amber-500 font-semibold text-neutral-950 hover:bg-amber-600 sm:w-auto'
                    disabled={
                      lockInteractionsDisabled ||
                      !paymentRunApproveLockEnabled ||
                      invoiceIdsPendingApprove.length === 0 ||
                      !nextRunAt ||
                      lockSelectionApprovePreview == null
                    }
                    onClick={onOpenApproveModalForSelection}
                    title={
                      invoiceIdsPendingApprove.length === 0
                        ? incrementalLockAllowed
                          ? 'Select charges not yet in the approved list to add them'
                          : 'Select at least one charge (Ready or Fee cap) to approve'
                        : undefined
                    }
                  >
                    <Lock className='mr-2 size-4' aria-hidden />
                    Approve &amp; Lock Run
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
